/**
 * Pro-Folie-Gestaltung (überschreibt das Quiz-Theme punktuell) – AhaSlides-artig.
 * Wird an die Clients (Beamer/Teilnehmer) durchgereicht und dort angewandt.
 */
export interface SlideStyle {
  /** Hintergrundquelle: Theme (Standard), einfarbig oder Bild/GIF. */
  bgKind?: 'theme' | 'color' | 'image';
  bgColor?: string;
  bgImage?: string;
  /** Schriftart-Stack (Anzeige/Überschrift). */
  fontDisplay?: string;
  /** Eigene Akzentfarbe nur für diese Folie. */
  accent?: string;
  /** Textfarbe der Folie (falls abweichend vom Theme). */
  textColor?: string;
  /** Ausrichtung des Fragetexts. */
  align?: 'left' | 'center';
  /** Einblend-Animation der Folie (Beamer/Teilnehmer). */
  anim?: SlideAnim;
  /**
   * Bild-Enthüllung: das Fragebild wird während der Antwortzeit schrittweise
   * aufgedeckt (Ratespiel). Wirkt auf Beamer und Teilnehmergerät gleichermaßen.
   */
  reveal?: SlideReveal;
  /** Verschleierungs-Stärke am Anfang (1 = dezent … 5 = extrem; Standard 3). */
  revealIntensity?: number;
  /** Frei platzierbare Zusatzelemente (Text/Bild/GIF) auf der Folie. */
  elements?: SlideElement[];
  /**
   * Optionale freie Platzierung/Größe des Fragetexts (Titel). Ist dies gesetzt,
   * wird der Fragetext absolut positioniert statt im normalen Fluss angezeigt –
   * auf allen Oberflächen konsistent.
   */
  titleLayout?: TitleLayout;
}

/** Einblend-Animation einer Folie. */
export type SlideAnim = 'fade' | 'slide' | 'zoom' | 'none';

/** CSS-Klasse für die gewählte Folien-Animation (leer = Standard/keine Ganz-Folien-Animation). */
export function slideAnimClass(anim?: SlideAnim | null): string {
  switch (anim) {
    case 'fade': return 'lq-fx-fade';
    case 'slide': return 'lq-fx-slide';
    case 'zoom': return 'lq-fx-zoom';
    default: return '';
  }
}

/** Art der Bild-Enthüllung während der Antwortzeit. */
export type SlideReveal = 'blur' | 'tiles' | 'pixelate';

/** Auswahloptionen für die Bild-Enthüllung (Editor). */
export const SLIDE_REVEAL_OPTIONS: { id: SlideReveal | 'default'; label: string }[] = [
  { id: 'default', label: 'Aus (Bild sofort sichtbar)' },
  { id: 'blur', label: 'Weichzeichner (langsam scharf)' },
  { id: 'pixelate', label: 'Verpixelt (Mosaik wird fein)' },
  { id: 'tiles', label: 'Kacheln (Stück für Stück)' },
];

/** Stärke-Stufen der Verschleierung (Editor). */
export const REVEAL_INTENSITY_OPTIONS: { id: number; label: string }[] = [
  { id: 1, label: 'Dezent' },
  { id: 2, label: 'Leicht' },
  { id: 3, label: 'Mittel' },
  { id: 4, label: 'Stark' },
  { id: 5, label: 'Extrem' },
];

const clampIntensity = (i?: number): number => Math.min(5, Math.max(1, Math.round(i ?? 3)));

/**
 * Ease-in-Kurve für die Enthüllung: bleibt am Anfang lange stark verschleiert
 * und wird erst später (gegen Ende der Zeit) schnell klar. 0→0, 1→1.
 */
export function revealEase(p: number): number {
  const x = Math.min(1, Math.max(0, p));
  return Math.pow(x, 3); // steile Kurve: lange stark verschleiert, dann schnell klar
}

/** Anfangs-Weichzeichnung (als Vielfaches der Bildbreite, z. B. für px/cqw). */
export function revealBlurUnits(intensity?: number): number {
  return 2.4 + clampIntensity(intensity) * 2.2; // 4.6 … 13.4
}

/** Kachelraster je Stärke (stärker = feiner ⇒ Bild bleibt länger unkenntlich). */
export function revealTileGrid(intensity?: number): { cols: number; rows: number } {
  const grid: Record<number, [number, number]> = { 1: [5, 3], 2: [8, 5], 3: [12, 8], 4: [18, 11], 5: [26, 16] };
  const [cols, rows] = grid[clampIntensity(intensity)]!;
  return { cols, rows };
}

/**
 * Mosaik-Blockanzahl (horizontal) für die Verpixelung zu einem Fortschritt 0..1.
 * Startet grob (je nach Stärke, Extrem = nur 2 Blöcke) und wird bis scharf.
 * Erwartet bereits einen ge-eas-ten Fortschritt (siehe revealEase).
 */
export function revealPixelBlocks(intensity: number | undefined, progress: number): number {
  const start: Record<number, number> = { 1: 22, 2: 14, 3: 9, 4: 5, 5: 2 };
  const s = start[clampIntensity(intensity)]!;
  const target = 160; // praktisch scharf
  return Math.max(2, Math.round(s + (target - s) * Math.min(1, Math.max(0, progress))));
}

/**
 * Enthüllungs-Fortschritt 0..1 aus der verbleibenden Antwortzeit – mit Ease-in,
 * damit das Bild am Anfang wirklich schwer erkennbar ist und erst spät klar wird.
 * `revealed`/kein Zeitfenster ⇒ 1 (vollständig sichtbar).
 */
export function revealProgress(remainingMs: number, timeLimitS: number): number {
  if (timeLimitS <= 0) return 1;
  const total = timeLimitS * 1000;
  const elapsed = 1 - remainingMs / total; // 0..1 des Zeitfensters
  // Enthüllung schon bei ~80 % der Zeit vollständig abschließen, damit das
  // komplette Bild noch sichtbar ist, bevor zur Auflösung gewechselt wird.
  return revealEase(Math.min(1, Math.max(0, elapsed / 0.8)));
}

/**
 * Deterministische Aufdeck-Reihenfolge für die Kachel-Enthüllung: liefert je
 * Kachelindex den Rang (0 = zuerst sichtbar). Seed hält die Reihenfolge pro
 * Frage stabil und auf allen Geräten identisch.
 */
export function tileRevealRanks(count: number, seed: number): number[] {
  const order = Array.from({ length: count }, (_, i) => i);
  let s = (seed | 0) ^ 0x9e3779b9;
  for (let i = count - 1; i > 0; i--) {
    s = (s * 1664525 + 1013904223) | 0;
    const j = Math.abs(s) % (i + 1);
    const tmp = order[i]!;
    order[i] = order[j]!;
    order[j] = tmp;
  }
  const rank = new Array<number>(count);
  order.forEach((tileIndex, position) => { rank[tileIndex] = position; });
  return rank;
}

/** Auswahloptionen für die Folien-Animation (Editor). */
export const SLIDE_ANIM_OPTIONS: { id: SlideAnim | 'default'; label: string }[] = [
  { id: 'default', label: 'Standard' },
  { id: 'fade', label: 'Einblenden' },
  { id: 'slide', label: 'Von unten' },
  { id: 'zoom', label: 'Zoom' },
  { id: 'none', label: 'Keine' },
];

/** Freie Platzierung des Fragetexts (Titel). Werte wie bei SlideElement. */
export interface TitleLayout {
  x: number; // % der Breite
  y: number; // % der Höhe
  w: number; // Breite in % der Folie
  fontSize?: number; // cqw
  fontDisplay?: string; // eigener Schrift-Stack (sonst Folien-/Theme-Schrift)
  color?: string;
}

/**
 * Frei platzierbares Overlay-Element. Positionen/Größen in Prozent der Folie,
 * damit sie zwischen Editor-Vorschau und Beamer konsistent skalieren.
 * Schriftgröße in `cqw` (Container-Breite), ebenfalls skaliert.
 */
export interface SlideElement {
  id: string;
  kind: 'text' | 'image';
  x: number; // 0..100 (linke obere Ecke, % der Breite)
  y: number; // 0..100 (% der Höhe)
  w: number; // Breite in % der Folie
  rotation?: number;
  // text:
  text?: string;
  color?: string;
  fontSize?: number; // in cqw (relativ zur Folienbreite)
  fontDisplay?: string; // eigener Schrift-Stack (sonst Folien-/Theme-Schrift)
  bold?: boolean;
  // image / gif:
  src?: string;
}

export interface FontOption {
  id: string;
  label: string;
  stack: string;
}

/**
 * Kuratierte, systemweit meist verfügbare Schriften (offline, keine CDN-Fonts).
 * Eigene Schriftdateien lassen sich später ergänzen.
 */
export const FONT_OPTIONS: FontOption[] = [
  { id: 'system', label: 'System (Standard)', stack: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" },
  { id: 'trebuchet', label: 'Trebuchet (rund)', stack: "'Trebuchet MS', system-ui, sans-serif" },
  { id: 'verdana', label: 'Verdana (klar)', stack: "Verdana, Geneva, sans-serif" },
  { id: 'georgia', label: 'Georgia (Serif)', stack: "Georgia, 'Times New Roman', serif" },
  { id: 'times', label: 'Times (klassisch)', stack: "'Times New Roman', Times, serif" },
  { id: 'impact', label: 'Impact (plakativ)', stack: "Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif" },
  { id: 'courier', label: 'Courier (Monospace)', stack: "'Courier New', Courier, monospace" },
  { id: 'comic', label: 'Comic (locker)', stack: "'Comic Sans MS', 'Comic Sans', cursive" },
];

export function fontStackById(id: string | null | undefined): string | undefined {
  return FONT_OPTIONS.find((f) => f.id === id)?.stack;
}

/**
 * Eigene, hochgeladene Schriftdatei (offline, aus /uploads). Wird quizweit in
 * `themeOverrides.customFonts` (JSON) abgelegt und auf allen Oberflächen per
 * @font-face registriert.
 */
export interface CustomFont {
  family: string; // eindeutiger Familienname, z. B. "cf-ab12"
  label: string; // Anzeigename (Dateiname)
  url: string; // /uploads/<datei>
}

/** Liest die quizweiten Eigenschriften aus den themeOverrides (fehlertolerant). */
export function parseCustomFonts(overrides?: Record<string, string> | null): CustomFont[] {
  const raw = overrides?.customFonts;
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr.filter((f): f is CustomFont => Boolean(f && (f as CustomFont).family && (f as CustomFont).url));
  } catch {
    return [];
  }
}

/** CSS-Font-Stack für eine Eigenschrift (mit System-Fallback). */
export function customFontStack(f: CustomFont): string {
  return `"${f.family}", system-ui, sans-serif`;
}

/**
 * Design-Vorlage: fertiger Folien-Look zum Anklicken (AhaSlides-artig).
 * Wird auf die aktuelle Folie angewandt und überschreibt Hintergrund, Farben
 * und Schrift – frei platzierte Elemente/Titel bleiben erhalten.
 */
export interface SlideTemplate {
  id: string;
  name: string;
  /** Kleine Vorschau (CSS-Hintergrund) für die Auswahl. */
  swatch: string;
  style: Partial<SlideStyle>;
}

export const SLIDE_TEMPLATES: SlideTemplate[] = [
  {
    id: 'clean',
    name: 'Klar',
    swatch: '#ffffff',
    style: { bgKind: 'color', bgColor: '#ffffff', textColor: '#0b1020', accent: '#3b7ff5', align: 'center', fontDisplay: undefined },
  },
  {
    id: 'midnight',
    name: 'Mitternacht',
    swatch: 'linear-gradient(135deg,#0b1020,#243b6b)',
    style: { bgKind: 'color', bgColor: 'linear-gradient(135deg,#0b1020,#243b6b)', textColor: '#ffffff', accent: '#6ea8ff', align: 'center' },
  },
  {
    id: 'sunset',
    name: 'Sonnenuntergang',
    swatch: 'linear-gradient(135deg,#ff7e5f,#feb47b)',
    style: { bgKind: 'color', bgColor: 'linear-gradient(135deg,#ff7e5f,#feb47b)', textColor: '#3a1d10', accent: '#c0392b', align: 'center' },
  },
  {
    id: 'ocean',
    name: 'Ozean',
    swatch: 'linear-gradient(135deg,#2193b0,#6dd5ed)',
    style: { bgKind: 'color', bgColor: 'linear-gradient(135deg,#2193b0,#6dd5ed)', textColor: '#04222b', accent: '#0b6b86', align: 'center' },
  },
  {
    id: 'forest',
    name: 'Wald',
    swatch: 'linear-gradient(135deg,#134e5e,#71b280)',
    style: { bgKind: 'color', bgColor: 'linear-gradient(135deg,#134e5e,#71b280)', textColor: '#ffffff', accent: '#a7e0b0', align: 'center' },
  },
  {
    id: 'contrast',
    name: 'Kontrast',
    swatch: 'linear-gradient(135deg,#111,#111)',
    style: { bgKind: 'color', bgColor: '#0a0a0a', textColor: '#ffe600', accent: '#ffe600', align: 'center', fontDisplay: "Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif" },
  },
  {
    id: 'paper',
    name: 'Papier',
    swatch: '#f4f1ea',
    style: { bgKind: 'color', bgColor: '#f4f1ea', textColor: '#2b2b2b', accent: '#b5533a', align: 'left', fontDisplay: "Georgia, 'Times New Roman', serif" },
  },
  {
    id: 'candy',
    name: 'Bonbon',
    swatch: 'linear-gradient(135deg,#ff9a9e,#fecfef)',
    style: { bgKind: 'color', bgColor: 'linear-gradient(135deg,#ff9a9e,#fecfef)', textColor: '#5a1a3a', accent: '#d6336c', align: 'center', fontDisplay: "'Comic Sans MS', 'Comic Sans', cursive" },
  },
];
